import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useTheme } from '@mui/material';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';

import ConfirmationDialog from 'src/components/ConfirmationDialog';
import { useDeleteCandidateCertificate } from 'src/pages/CandidatesDetailsPage/api';

import messages from './messages';

interface Props {
  userUuid: string;
  certificate: {
    code: string;
    id: string;
    title: string;
  };
}

export default function CandidateProjectCertificate({
  userUuid,
  certificate,
}: Props) {
  const theme = useTheme();
  const { formatMessage } = useIntl();

  const { mutate: deleteCertificate, isPending: isDeletingCertificate } =
    useDeleteCandidateCertificate();

  const handleDelete = useCallback(async () => {
    return new Promise<boolean>((resolve) => {
      deleteCertificate(
        {
          uuid: userUuid,
          certificateCode: certificate.code,
        },
        {
          onSuccess: () => {
            resolve(true);
          },
          onError: () => resolve(false),
        },
      );
    });
  }, [userUuid, certificate.code]);

  return (
    <Box
      p={1}
      border="1px solid"
      borderRadius={1.5}
      width="fit-content"
      borderColor={theme.palette.grey[500]}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h5">{certificate.title}</Typography>
        <ConfirmationDialog
          onConfirm={handleDelete}
          message={formatMessage(messages.deleteCertificateConfirmMessage)}
        >
          {(handleDialogOpen) => (
            <IconButton
              onClick={handleDialogOpen}
              disabled={isDeletingCertificate}
            >
              <DeleteForeverOutlinedIcon fontSize="small" />
            </IconButton>
          )}
        </ConfirmationDialog>
      </Stack>
    </Box>
  );
}
